import type { WebDataEvent } from '../../shared/types'

const INITIAL = 10
const MAX = 30_000
const FACTOR = 2

type EnqueuedSender = {
  enqueueEvent: (event: WebDataEvent) => void
  start: () => void
}

export const createEnqueuedSender = (sender: (event: WebDataEvent) => void): EnqueuedSender => {
  const queue: WebDataEvent[] = []
  let initialized = false

  let backoff = INITIAL
  let flushing = false
  let retryTimer: ReturnType<typeof setTimeout> | null = null

  const flush = () => {
    if (flushing || !queue.length) return
    flushing = true

    try {
      while (queue.length) {
        try {
          const event = queue[0]
          sender(event)
          queue.shift()
          backoff = INITIAL
        } catch {
          if (retryTimer || flushing) return
          retryTimer = setTimeout(() => {
            retryTimer = null
            flush()
          }, backoff)
          backoff = Math.min(backoff * FACTOR, MAX)
          return
        }
      }
    } finally {
      flushing = false
    }
  }

  const enqueueEvent = (event: WebDataEvent) => {
    queue.push(event)
    if (!initialized) return
    flush()
  }

  const start = () => {
    initialized = true
    flush()
  }

  return { enqueueEvent, start }
}
